/* jshint esversion: 6 */
class DataBridgeServiceModule {
    /**
     * Constructs the DataBridgeServiceModule.
     * @param {Object} LogBridgeService - Service for logging.
     * @param {Object} $injector - AngularJS injector service.
     * @param {Object} $signals - Signal handling service.
     * @param {Object} $timeout - AngularJS timeout service.
     * @param {Object} $q - AngularJS promise service.
     * @param {Object} ResponseDataBridgeClient - Bridge client for response data.
     */
    constructor(LogBridgeService, $injector, $signals, $timeout, $q, ResponseDataBridgeClient) {
        this.instanceId = Math.random().toString(36).substring(2, 15) + Math.random().toString(36).substring(2, 15);        

        this._logIdentifier = 'DataBridgeService';
        this.$logger = LogBridgeService;
        this.$injector = $injector;
        this.$signals = $signals;
        this.$timeout = $timeout;
        this.$q = $q;
        this.ResponseDataBridgeClient = ResponseDataBridgeClient;

        this.$logger.info(this, 'initialize');

        this._hasExistingDataPromise = null;
        this._hasExistingDataCache = null;
    }

    /**
     * Packs the response data into a file with the given name.
     * @param {string} fileName - The name of the file to pack the response into.
     * @returns {Promise} - A promise that resolves with the packed response data when complete, 
     *                     or rejects if there is an error during packing.
     *                     The resolved value may contain {content, blob} if the response includes file data.
     */
    packResponse(fileName, recoveryMetadata) {
        const deferred = this.$q.defer();

        this.$logger.info(this, 'packResponse("' + fileName + '")');

        // Clean up previous signal handlers to prevent accumulation across calls
        if (this._afterPackedHandler) this.$signals.remove('afterPacked', this._afterPackedHandler);
        if (this._afterPackedErrorHandler) this.$signals.remove('afterPackedError', this._afterPackedErrorHandler);

        this._afterPackedHandler = (result) => {
            console.log('DataBridgeService.packResponse() afterPacked', result);
            deferred.resolve(result);
        };
        this._afterPackedErrorHandler = (error) => {
            console.log('DataBridgeService.packResponse() afterPackedError', error);
            const $rootScope = this.$injector.get('$rootScope');
            const reject = () => deferred.reject(error);

            // Reject inside Angular when possible so downstream handlers update
            // the view in the same digest instead of relying on a later tick.
            if ($rootScope && typeof $rootScope.$safeApply === 'function') {
                $rootScope.$safeApply(reject);
                return;
            }

            reject();

            // Raw setTimeout + $rootScope.$apply bypasses Sentry/OpenReplay
            // instrumentation that can intercept $timeout and prevent the
            // $q rejection from being processed in a digest cycle.
            setTimeout(() => {
                try {
                    $rootScope.$apply();
                } catch (e) {
                    // "$digest already in progress" is expected here; log anything else.
                    if (e && e.message && !e.message.includes('$digest') && !e.message.includes('$apply')) {
                        console.error('DataBridgeService.afterPackedError $apply failed:', e);
                    }
                }
            }, 0);
        };

        this.$signals.on('afterPacked', this._afterPackedHandler);
        this.$signals.on('afterPackedError', this._afterPackedErrorHandler);

        this.ResponseDataBridgeClient.packResponse(fileName, recoveryMetadata);

        return deferred.promise;
    }

    /**
     * Initializes the response.
     * @param {boolean} useExistingData - Whether to use existing data.
     * @param {boolean} useHybridResponse - Whether to use a hybrid response.
     * @returns {Promise} - A promise that resolves when the initialization is complete.
     */
    initResponse(useExistingData, useHybridResponse) {
        console.log("DataBridgeService.initResponse()");

        const TaskInfoService = this.$injector.get('TaskInfoService'),
            taskInfo = TaskInfoService.getTaskInfo();
        if (angular.isDefined(taskInfo) && taskInfo && angular.isFunction(taskInfo.isMediaAssessment) && taskInfo.isMediaAssessment()) {
            useHybridResponse = false;
        }

        const deferred = this.$q.defer();
        this.$logger.info(this, 'initResponse(', useExistingData, useHybridResponse, ")");

        const onResponseInitialised = function () {
            deferred.resolve();
            this.$signals.remove(IB.Signals.RESPONSE_INITIALIZED, onResponseInitialised);
            this.$signals.remove(IB.Signals.RESPONSE_INIT_FAILED, onResponseInitFailed);
        }.bind(this);
        this.$signals.on(IB.Signals.RESPONSE_INITIALIZED, onResponseInitialised);

        const onResponseInitFailed = function () {
            deferred.reject();
            this.$signals.remove(IB.Signals.RESPONSE_INITIALIZED, onResponseInitialised);
            this.$signals.remove(IB.Signals.RESPONSE_INIT_FAILED, onResponseInitFailed);
        }.bind(this);
        this.$signals.on(IB.Signals.RESPONSE_INIT_FAILED, onResponseInitFailed);

        const shouldUseExistingData = angular.isDefined(useExistingData) ? useExistingData : false,
            shouldUseHybridResponse = angular.isDefined(useHybridResponse) ? useHybridResponse : false;

        this.ResponseDataBridgeClient.initResponse(shouldUseExistingData, shouldUseHybridResponse);

        return deferred.promise;
    }

    /**
     * Checks if there is existing data.
     * @param {boolean} allowHybridResponse - Whether to allow hybrid response.
     * @param {string} hybridResponseURL - The URL for the hybrid response.
     * @param {string} pirateToken - The token for pirate access.
     * @param {boolean} allowCache - Whether to allow caching.
     * @returns {Promise} - A promise that resolves with the result of the check.
     */
    hasExistingData(allowHybridResponse, hybridResponseURL, pirateToken, allowCache) {
        this._hasExistingDataPromise = this.$q.defer();

        this.$logger.info(this, 'hasExistingData(', allowHybridResponse, hybridResponseURL, pirateToken, ")");
        console.log('hasExistingData(', allowHybridResponse, hybridResponseURL, pirateToken, ")");

        if (!angular.isDefined(allowCache)) {
            allowCache = false;
        }

        if (allowCache && angular.isObject(this._hasExistingDataCache)) {
            console.log("Returning hasExistingData cached result", this._hasExistingDataCache);
            this._hasExistingDataPromise.resolve(this._hasExistingDataCache);
            return this._hasExistingDataPromise.promise;
        }

        this.$signals.remove("hasExistingDataResult", this.onHasExistingDataResult.bind(this));
        this.$signals.on("hasExistingDataResult", this.onHasExistingDataResult.bind(this));
        this.ResponseDataBridgeClient.hasExistingData(allowHybridResponse, hybridResponseURL, pirateToken);

        return this._hasExistingDataPromise.promise;
    }

    /**
     * Handles the result of the hasExistingData check.
     * @param {boolean} hasExistingData - Whether existing data is present.
     * @param {boolean} hasExistingDataOnDisk - Whether existing data is on disk.
     * @param {boolean} hasExistingDataOnWebService - Whether existing data is on web service.
     * @param {number} unixTimeStampUTC - The timestamp of the data.
     */
    onHasExistingDataResult(hasExistingData, hasExistingDataOnDisk, hasExistingDataOnWebService, unixTimeStampUTC, candidateIdentity) {
        this._hasExistingDataCache = {
            hasExistingData: hasExistingData,
            hasExistingDataOnDisk: hasExistingDataOnDisk,
            hasExistingDataOnWebService: hasExistingDataOnWebService,
            unixTimeStampUTC: unixTimeStampUTC,
            candidateIdentity: candidateIdentity
        };

        if (this._hasExistingDataPromise) {
            this._hasExistingDataPromise.resolve(this._hasExistingDataCache);
        }
    }

    /**
     * Gets the cached result of the hasExistingData check.
     * @returns {Object} - The cached result.
     */
    hasExistingDataCache() {
        return this._hasExistingDataCache;
    }

    /**
     * Backs up existing response data to a separate IndexedDB and clears current storage.
     * @returns {Promise} - A promise that resolves when the backup is complete.
     */
    backupAndClearResponseData() {
        const deferred = this.$q.defer();

        const onComplete = (backupDbName) => {
            cleanup();
            deferred.resolve(backupDbName);
        };
        const onFailed = (error) => {
            cleanup();
            deferred.reject(error);
        };
        const cleanup = () => {
            this.$signals.remove('backupResponseDataComplete', onComplete);
            this.$signals.remove('backupResponseDataFailed', onFailed);
            if (timeoutHandle) this.$timeout.cancel(timeoutHandle);
        };

        this.$signals.on('backupResponseDataComplete', onComplete);
        this.$signals.on('backupResponseDataFailed', onFailed);

        // Timeout after 30 seconds to prevent permanent hang if service worker doesn't respond
        const timeoutHandle = this.$timeout(() => {
            cleanup();
            deferred.reject('Backup operation timed out');
        }, 30000);

        this.ResponseDataBridgeClient.backupAndClearResponseData();

        return deferred.promise;
    }

    /**
     * Clears the cached hasExistingData result.
     */
    clearHasExistingDataCache() {
        this._hasExistingDataCache = null;
    }

    /**
     * Puts data into the bridge.
     * @param {string} key - The key for the data.
     * @param {any} value - The value to be stored.
     */
    async put(key, value) {
        //console.error("DataBridgeService.put", key);
        await this.ResponseDataBridgeClient.setData(key, JSON.stringify(value, null, " "));
    }

    async waitForPendingWrites(timeoutMs) {
        return this.ResponseDataBridgeClient.waitForPendingWrites(timeoutMs);
    }

    /**
     * Gets data from the bridge.
     * @param {string} key - The key for the data.
     * @returns {string} - The value associated with the key.
     */
    get(key) {
        //console.error("DataBridgeService.get", key);
        const serResult = this.ResponseDataBridgeClient.getData(key);
        
        
        if (angular.isString(serResult)) {
            return serResult === "" ? serResult : JSON.parse(serResult);
        } else {
            return serResult;
        }
    }
}

export default DataBridgeServiceModule;
